import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Eye, Search, Check, Loader2, Folders, Folder, ChevronRight } from 'lucide-react';
import { useHookTrackWizardStore } from '@/store/hook-track/wizardStore';
import { useAuth } from '@/hooks/useAuth';
import { useHookTrackVideos } from '@/hooks/useHookTrackVideos';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { CompactFolderItem } from '@/components/video-editor/CompactFolderItem';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';
import { useQuery } from '@tanstack/react-query';

const formatViews = (views: number) => {
  if (views >= 1000) {
    return `${(views / 1000).toFixed(1).replace('.0', '')}k visualizações`;
  }
  return `${views} visualizações`;
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function VideoMainStep() {
  useLanguage();
  const { data, setMainVideo } = useHookTrackWizardStore();
  const { user } = useAuth();
  const { videos: userVideos, isLoading, error } = useHookTrackVideos(user?.id_user || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

  // ── Pastas raiz ────────────────────────────────────────────────────────────
  const { data: foldersData } = useQuery({
    queryKey: ['hook-track-wizard-folders'],
    queryFn: () => hierarchicalFoldersService.listRoot(),
    enabled: !!user?.id_user,
  });
  const rootFolders = foldersData?.items || [];

  // ── Detalhes da pasta selecionada ─────────────────────────────────────────
  const { data: folderDetails, isLoading: isFolderLoading } = useQuery({
    queryKey: ['hook-track-wizard-folder', currentFolderId],
    queryFn: () => hierarchicalFoldersService.getFolderDetails(currentFolderId!),
    enabled: !!currentFolderId,
  });

  const folderVideoIds = useMemo(() => {
    if (!folderDetails?.videos) return null;
    return new Set(folderDetails.videos.map(v => v.id));
  }, [folderDetails]);

  const handleSelectVideo = (video: any) => {
    setMainVideo({
      id: video.id,
      name: video.title,
      thumbnail: video.thumbnailUrl || '',
      duration: video.duration,
      views: video.views,
    });
  };

  // ── Vídeos filtrados ───────────────────────────────────────────────────────
  const filteredVideos = useMemo(() => {
    let base = userVideos;

    if (currentFolderId && folderVideoIds) {
      base = base.filter(v => folderVideoIds.has(v.id));
    }

    if (searchQuery) {
      base = base.filter(v => v.title.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    return base;
  }, [userVideos, currentFolderId, folderVideoIds, searchQuery]);

  const isLoadingVideos = isLoading || (!!currentFolderId && isFolderLoading);

  return (
    <div className="flex flex-col animate-fade-in pb-6 h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6">
        <h2 className="text-[18px] lg:text-[20px] font-semibold text-[#4B5563]">
          {translateInterfaceText("Defina o vídeo Principal")}</h2>
        <p className="text-[15px] text-[#6B7280] mt-1.5 leading-relaxed">
          {translateInterfaceText("Selecione a parte da VSL sem a introdução. Assim, você poderá testar diferentes começos.")}</p>
      </div>

      {/* Área principal: sidebar + vídeos */}
      <div className="flex flex-row flex-1 overflow-hidden mt-6 px-4 sm:px-6 lg:px-8 min-h-0 gap-4">

        {/* Sidebar — Pastas */}
        <aside className="hidden md:flex flex-col w-[180px] flex-shrink-0 border border-[#E5E7EB] rounded-[12px] overflow-y-auto bg-white">
          {/* Todos os vídeos */}
          <div
            onClick={() => { setCurrentFolderId(null); setSearchQuery(''); }}
            className={`group flex items-center gap-2.5 px-3 py-3 min-h-[48px] cursor-pointer transition-all duration-150 ease-out border-l-[3px] rounded-tl-[12px] ${
              currentFolderId === null ? 'border-l-[#2D6CF6]' : 'border-l-transparent hover:text-[#2D6CF6]'
            }`}
            role="button"
            tabIndex={0}
          >
            <Folders className={`w-4 h-4 flex-shrink-0 stroke-[1.5px] ${currentFolderId === null ? 'text-[#2D6CF6]' : 'text-[#6B7280] group-hover:text-[#2D6CF6]'}`} />
            <span className={`text-[13px] truncate ${currentFolderId === null ? 'text-[#2D6CF6] font-semibold' : 'text-[#374151] font-medium group-hover:text-[#2D6CF6]'}`}>
              {translateInterfaceText("Todos")}</span>
          </div>

          {rootFolders.map(folder => (
            <CompactFolderItem
              key={folder.id}
              id={folder.id}
              name={folder.name}
              videoCount={folder.videosCount || 0}
              onClick={(id) => { setCurrentFolderId(id); setSearchQuery(''); }}
              isActive={currentFolderId === folder.id}
            />
          ))}
        </aside>

        {/* Conteúdo: busca + grid */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0">
          {/* Busca */}
          <div className="relative mb-3 flex-shrink-0 w-full max-w-[300px] min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-[#9CA3AF]/60" />
            <Input
              placeholder={translateInterfaceText("Buscar vídeo...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9 pr-3 bg-[#F9FAFB] border-[#E8E8E8] rounded-lg text-[13px] placeholder:text-[#9CA3AF]/70 focus:border-[#1D4FFF] focus:ring-1 focus:ring-[#1D4FFF]/10 focus:bg-white transition-all duration-200"
            />
          </div>

          {/* Breadcrumb — quando dentro de uma pasta */}
          {currentFolderId && folderDetails && (
            <nav className="flex items-center gap-1 text-[12px] mb-3 flex-wrap flex-shrink-0">
              <button
                type="button"
                onClick={() => { setCurrentFolderId(null); setSearchQuery(''); }}
                className="text-[#1D4FFF] hover:text-[#1A45E5] font-medium transition-colors"
              >
                {translateInterfaceText("Todos")}</button>
              {folderDetails.breadcrumb.map((item) => (
                <React.Fragment key={item.id}>
                  <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
                  <button
                    type="button"
                    onClick={() => { setCurrentFolderId(item.id); setSearchQuery(''); }}
                    className="text-[#1D4FFF] hover:text-[#1A45E5] font-medium transition-colors truncate max-w-[140px]"
                  >
                    {item.name}
                  </button>
                </React.Fragment>
              ))}
              <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
              <span className="text-[#374151] font-semibold truncate max-w-[160px]">{folderDetails.name}</span>
            </nav>
          )}

          {/* Grid de vídeos */}
          <ScrollArea className="flex-1 -mx-1 px-1">
            {/* Subpastas — exibidas acima dos vídeos quando a pasta atual tem filhas */}
            {currentFolderId && !searchQuery && folderDetails?.children && folderDetails.children.length > 0 && (
              <div className="mb-4 px-1">
                <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2">{translateInterfaceText("Subpastas")}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {folderDetails.children.map(child => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => { setCurrentFolderId(child.id); setSearchQuery(''); }}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-[10px] border border-[#E5E7EB] hover:border-[#1D4FFF] hover:bg-[#EBF5FF] transition-colors text-left"
                    >
                      <Folder className="w-4 h-4 text-[#6B7280] flex-shrink-0" />
                      <span className="text-[13px] truncate flex-1 text-[#374151]">{child.name}</span>
                      <span className="text-[11px] text-[#9CA3AF] flex-shrink-0">{child.videosCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoadingVideos ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-[#1D4FFF]" />
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
                  <Search className="w-6 h-6 text-red-400" />
                </div>
                <p className="text-[15px] text-red-600 font-medium">{translateInterfaceText("Erro ao carregar vídeos")}</p>
                <p className="text-[13px] text-[#9CA3AF] mt-1">{error}</p>
              </div>
            ) : filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-1 pb-4 px-1">
                {filteredVideos.map((video) => {
                  const isSelected = data.mainVideo?.id === video.id;
                  const isBlocked = video.inUse;

                  return (
                    <div
                      key={video.id}
                      className={cn(
                        "group rounded-[14px] overflow-hidden transition-all duration-200",
                        !isBlocked && "hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] cursor-pointer",
                        isSelected && "ring-2 ring-[#1D4FFF] ring-offset-2 shadow-[0_8px_24px_rgba(29,79,255,0.15)]",
                        isBlocked && "opacity-60 cursor-not-allowed"
                      )}
                      onClick={() => { if (!isBlocked) handleSelectVideo(video); }}
                    >
                      <div className="relative aspect-video bg-[#1A1A1A] rounded-[14px] overflow-hidden">
                        <img
                          src={video.thumbnailUrl || 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=400&h=225&auto=format&fit=crop'}
                          alt={video.title}
                          className={cn("w-full h-full object-cover transition-all duration-200", isBlocked && "grayscale")}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none z-[1]" />

                        {isBlocked && (
                          <div className="absolute inset-0 bg-black/40 z-[2] flex items-center justify-center">
                            <div className="bg-red-500/90 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
                              {video.inUseText}
                            </div>
                          </div>
                        )}

                        <div className="absolute bottom-10 left-2.5 z-[3] text-white text-[11px] font-medium bg-black/70 px-1.5 py-0.5 rounded">
                          {formatDuration(video.duration)}
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 p-3 z-[3]">
                          <h3 className="text-white font-semibold text-[14px] leading-tight line-clamp-1 mb-1 drop-shadow-sm">
                            {video.title}
                          </h3>
                          <div className="flex items-center gap-1.5 text-white/90">
                            <Eye className="h-3.5 w-3.5" />
                            <span className="text-[12px] font-medium">{formatViews(video.views)}</span>
                          </div>
                        </div>

                        {isSelected && !isBlocked && (
                          <div className="absolute top-2.5 right-2.5 z-[5] w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border border-white">
                            <Check className="w-4 h-4 text-[#1D4FFF]" strokeWidth={3} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#F3F4F6] flex items-center justify-center mb-4">
                  <Search className="w-6 h-6 text-[#9CA3AF]" />
                </div>
                <p className="text-[15px] text-[#6B7280] font-medium">{translateInterfaceText("Nenhum vídeo encontrado")}</p>
                <p className="text-[13px] text-[#9CA3AF] mt-1">
                  {currentFolderId ? translateInterfaceText("Esta pasta não possui vídeos") : translateInterfaceText("Tente buscar por outro termo ou campanha")}
                </p>
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
